/**
 * <SeoLandingPage> — renders an auto-generated long-tail landing at /care/{slug}.
 *
 * Wired to `GET /api/seo/landing/{slug}` (only approved pages return 200; drafts
 * + needs_review + rejected → 404, by design, so they don't pollute the index
 * even if a URL is guessed). The page reuses the same visual patterns as
 * LocalCarePage so we stay consistent — H1 + intro + trust badges + service
 * detail + local context + internal links + FAQ + CTA, served with full
 * Seo metadata (title, description, canonical, OG, Twitter, JSON-LD).
 *
 * Why a single dynamic route instead of build-time prerender:
 *   - 100+ pages can be created/edited without redeploying the frontend
 *   - The next 1k pages don't bloat the bundle
 *   - prerender.js can later be extended to crawl /care/{slug} URLs from the
 *     sitemap so they ALSO get static HTML, for crawlers that don't run JS
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, MapPin, ShieldCheck, CheckCircle2, Users, Loader2 } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { BRAND, absoluteUrl } from '../lib/siteConfig';
import { localServiceSchema, faqSchema, breadcrumbSchema } from '../lib/seoSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeoLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/seo/landing/${slug}`);
        if (!cancelled) setPage(r.data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center" data-testid="seo-landing-loading">
        <Loader2 className="w-6 h-6 text-cn-primary animate-spin" />
      </div>
    );
  }
  if (notFound || !page) {
    // Defensive: redirect to /home-care so a stale link still lands somewhere
    // useful instead of a 404 page (better for SEO + user experience).
    navigate('/home-care', { replace: true });
    return null;
  }

  const { service, city, content } = page;
  const path = `/care/${slug}`;
  const faqs = [
    {
      q: `Where can I find verified ${service.name.toLowerCase()} providers in ${city.name}, ${city.state}?`,
      a: `On CaraLoom. Every ${service.name.toLowerCase()} provider serving ${city.name} is identity-verified, license-validated, background-checked, and rated by the families they\u2019ve already cared for.`,
    },
    {
      q: `How much does ${service.name.toLowerCase()} cost in ${city.name}, ${city.state}?`,
      a: `Rates vary by acuity and certification. Pricing is shown up front on every clinician\u2019s profile \u2014 no agency markups, no hidden fees. You negotiate directly with the clinician.`,
    },
    {
      q: `How fast can ${service.name.toLowerCase()} start in ${city.name}?`,
      a: `Most ${city.name} families are matched within minutes. Initial visits typically begin within 24\u201348 hours depending on local availability.`,
    },
    {
      q: `Are CaraLoom ${service.name.toLowerCase()} providers in ${city.name} licensed?`,
      a: `Yes. We primary-source verify every clinician\u2019s license at signup and re-verify continuously \u2014 you see the verification badge on the profile itself.`,
    },
  ];

  const schema = [
    localServiceSchema({
      city: city.name, state: city.state, path,
      description: content.description,
    }),
    faqSchema(faqs),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' },
      { name: service.name, path: `/services/${service.slug}` },
      { name: `${city.name}, ${city.state}`, path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="seo-landing-page">
      <Seo
        title={content.title}
        description={content.description}
        path={path}
        canonical={absoluteUrl(path)}
        keywords={[
          `${service.name.toLowerCase()} ${city.name}`,
          `${service.name.toLowerCase()} ${city.name} ${city.state}`,
          `${service.name.toLowerCase()} near me ${city.name}`,
          `home ${service.name.toLowerCase()} ${city.name}`,
        ]}
        schema={schema}
      />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/services">Services</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/services/${service.slug}`}>{service.name}</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{city.name}, {city.state}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero — H1 + intro + CTA + trust badges */}
      <section className="cn-container pt-8 pb-10">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-cn-accent" />
            {city.name}, {city.state}
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5"
              data-testid="seo-landing-h1">
            {content.h1}
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed" data-testid="seo-landing-intro">
            {content.intro}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={`/auth?role=patient&need=${encodeURIComponent(service.name)}`}
                  className="cn-btn cn-btn-primary"
                  data-testid="seo-landing-cta-primary">
              Find {service.name.toLowerCase()} in {city.name} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={`/home-care/${city.slug}`} className="cn-btn cn-btn-ghost"
                  data-testid="seo-landing-cta-secondary">
              All home care in {city.name}
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> Background-checked</span>
            <span className="cn-trust-badge"><CheckCircle2 className="w-3.5 h-3.5" /> License-verified</span>
            {page.live_clinician_count_state > 0 && (
              <span className="cn-trust-badge" data-testid="seo-landing-supply-badge">
                <Users className="w-3.5 h-3.5" /> {page.live_clinician_count_state}+ verified in {city.state}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Internal links — pumps PageRank to canonical hubs */}
      <section className="cn-container py-12 border-t border-cn-border/60">
        <div className="cn-eyebrow mb-3">Related on CaraLoom</div>
        <h2 className="cn-display text-2xl sm:text-3xl text-cn-text mb-6">Keep exploring</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {content.internal_links.map((l) => (
            <Link key={l.href} to={l.href}
                  className="block p-5 rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 hover:shadow-md transition-shadow"
                  data-testid={`seo-landing-internal-${l.href.replace(/[^a-z0-9]/gi, '-')}`}>
              <p className="text-sm font-semibold text-cn-text dark:text-white">{l.label}</p>
              <p className="text-xs text-cn-primary mt-1 flex items-center gap-1">
                Open <ArrowRight className="w-3 h-3" />
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ — populates FAQPage JSON-LD + a real on-page Q&A block */}
      <section className="cn-container py-12 border-t border-cn-border/60">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">FAQ</div>
          <h2 className="cn-display text-2xl sm:text-3xl text-cn-text mb-6">
            {service.name} in {city.name} — common questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cn-container py-14 border-t border-cn-border/60">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="cn-display text-3xl sm:text-4xl text-cn-text mb-4">
            Ready to find {service.name.toLowerCase()} in {city.name}?
          </h2>
          <p className="text-cn-text-muted mb-7">
            Get matched in minutes with verified, background-checked clinicians serving {city.name}, {city.state}.
          </p>
          <Link to={`/auth?role=patient&need=${encodeURIComponent(service.name)}`}
                className="cn-btn cn-btn-primary"
                data-testid="seo-landing-cta-final">
            Get matched now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeoLandingPage;
